"use client";

/**
 * P14-10 · Whether the visitor is signed in, for client components.
 *
 * Mirrors `FeatureFlagsProvider`: hydrated once from the RSC layer, read
 * anywhere below without a round-trip. It exists because the alternative was
 * worse — `signedIn` is currently threaded by hand from server components
 * (`products/[slug]/page.tsx`, the storefront layout), which works for a page
 * that owns its children and does not for a `<ProductCard>` rendered three
 * levels inside a page-builder block. Wiring the card's wishlist heart by
 * prop-drilling would have meant a new prop on every grid, block and row that
 * has ever rendered a card, most of which have no business knowing about
 * sessions.
 *
 * This is a UX signal only. It picks which STORE the wishlist writes to (local
 * for a guest, the account for a shopper) and nothing else; every wishlist
 * procedure is `customerProcedure` and re-checks the real session server-side.
 * A lie here changes where a toggle is saved, never who it belongs to.
 *
 * ## P17A-06 · Where the initial value comes from, and why it is confirmed
 *
 * The storefront layout no longer calls `auth()` to fill `signedIn`; it passes
 * the header-only probe (`~/server/auth/session-probe`), which only knows that
 * a session COOKIE is present. That is the difference between HTML that is the
 * same for every anonymous visitor and HTML that is not: `auth()`'s answer
 * cannot be shared-cached, while a cookie-presence test on a cookieless
 * request always says `false` and can be.
 *
 * The cost is that the initial value can be wrong in exactly one direction — a
 * cookie that is expired, revoked (P17-08 bumps the session generation) or
 * forged reads as signed in. So this provider confirms it: when the probe says
 * yes, it asks `/api/auth/session` once on mount — the same endpoint Auth.js's
 * own `SessionProvider` uses — and replaces the guess with the real answer,
 * along with the display name the account menu needs. A cookieless visitor
 * makes no request at all, which is the common case and the cacheable one.
 */
import { createContext, useContext, useEffect, useState } from "react";

export interface AccountIdentity {
  /** Confirmed once `/api/auth/session` answers; the probe's guess until then. */
  signedIn: boolean;
  /** Display name / email. Null until confirmed, and for guests. */
  name: string | null;
  /** False while the confirmation request is still in flight. */
  confirmed: boolean;
}

const GUEST: AccountIdentity = { signedIn: false, name: null, confirmed: true };

const SignedInContext = createContext<AccountIdentity | null>(null);

/** The shape Auth.js's session endpoint answers with. */
interface SessionResponse {
  user?: { name?: string | null; email?: string | null } | null;
}

export function SignedInProvider({
  signedIn,
  children,
}: {
  signedIn: boolean;
  children: React.ReactNode;
}) {
  const [identity, setIdentity] = useState<AccountIdentity>(() =>
    signedIn ? { signedIn: true, name: null, confirmed: false } : GUEST,
  );

  useEffect(() => {
    if (!signedIn) {
      setIdentity(GUEST);
      return;
    }
    let cancelled = false;
    const confirm = async () => {
      try {
        const res = await fetch("/api/auth/session", {
          headers: { accept: "application/json" },
          // The session cookie is the entire point of the request.
          credentials: "same-origin",
        });
        if (!res.ok) return;
        const data = (await res.json()) as SessionResponse | null;
        if (cancelled) return;
        const user = data?.user;
        setIdentity(
          user
            ? {
                signedIn: true,
                name: user.name ?? user.email ?? null,
                confirmed: true,
              }
            : GUEST,
        );
      } catch {
        // A failed confirmation leaves the probe's guess standing rather than
        // signing the visitor out of the UI over one dropped request. Nothing
        // here grants access, so the optimistic state is the safe one.
      }
    };
    void confirm();
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  return (
    <SignedInContext.Provider value={identity}>
      {children}
    </SignedInContext.Provider>
  );
}

/**
 * Defaults to `false` outside a provider rather than throwing, unlike
 * `useFeatureFlags`. The failure modes are not symmetric: a missing flag
 * context means a component is rendering outside the app shell and should say
 * so loudly, whereas the honest answer to "is this visitor signed in?" with no
 * session context available is no.
 */
export function useSignedIn(): boolean {
  return useContext(SignedInContext)?.signedIn ?? false;
}

/** The same signal plus the display name, for the account menu (P17A-06). */
export function useAccountIdentity(): AccountIdentity {
  return useContext(SignedInContext) ?? GUEST;
}
